import React, { useEffect, useState, useCallback } from "react";
import { api, apiError, inr } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Download, Upload, RefreshCw, Send, CheckCircle2, XCircle, AlertTriangle, Save, FileDown } from "lucide-react";

const now = new Date();
const CUR_FY_START = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const FY_OPTIONS = Array.from({ length: 9 }, (_, i) => {
  const y = CUR_FY_START - i;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
});
const FIELDS = [["taxable", "Taxable"], ["igst", "IGST"], ["cgst", "CGST"], ["sgst", "SGST"], ["cess", "Cess"]];
const btn = "flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium disabled:opacity-50";

async function downloadBlob(url, fname) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = href; a.download = fname; a.click(); URL.revokeObjectURL(href);
    toast.success("Downloaded");
  } catch (e) { toast.error(apiError(e)); }
}

function UploadButton({ testid, label, onFile, disabled }) {
  return (
    <label data-testid={`${testid}-label`} className={`${btn} border border-border cursor-pointer hover:border-indigo-500/40 ${disabled ? "pointer-events-none opacity-50" : ""}`}>
      <Upload className="h-3.5 w-3.5" /> {label}
      <input data-testid={testid} type="file" accept=".xlsx" className="hidden" disabled={disabled}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
    </label>
  );
}

function WorkflowBar({ clientId, rt, keyStr, doc, role, onDone }) {
  const [busy, setBusy] = useState(false);
  const isChecker = role === "admin" || role === "partner";
  const p = (s) => s.toLowerCase();
  const act = async (action, body = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/annual/${rt}/${action}?key=${keyStr}`, body);
      toast.success(data.arn ? `Filed · ARN ${data.arn}` : `Status: ${data.status.replace(/_/g, " ")}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  if (!doc) return null;
  const st = doc.status;
  if (role === "reader") return st === "filed" ? <span data-testid={`${p(rt)}-arn`} className="text-xs font-mono text-emerald-300">ARN {doc.arn}</span> : null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {st === "prepared" && <button data-testid={`${p(rt)}-submit-button`} disabled={busy} onClick={() => act("submit")} className={`${btn} bg-amber-600 hover:bg-amber-500 text-white`}><Send className="h-3.5 w-3.5" /> Submit for Approval</button>}
      {st === "pending_approval" && isChecker && <>
        <button data-testid={`${p(rt)}-approve-button`} disabled={busy} onClick={() => act("approve")} className={`${btn} bg-cyan-600 hover:bg-cyan-500 text-white`}><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>
        <button data-testid={`${p(rt)}-reject-button`} disabled={busy} onClick={() => act("reject")} className={`${btn} border border-rose-500/40 text-rose-300`}><XCircle className="h-3.5 w-3.5" /> Reject</button>
      </>}
      {st === "pending_approval" && !isChecker && <span className="text-xs font-mono text-muted-foreground">Awaiting partner approval</span>}
      {st === "approved" && isChecker && <button data-testid={`${p(rt)}-file-button`} disabled={busy}
        onClick={() => window.confirm(`File ${rt} ${keyStr} on the GST portal? This is near-irreversible.`) && act("file", { confirm: true })}
        className={`${btn} bg-emerald-600 hover:bg-emerald-500 text-white`}>File on GST Portal</button>}
      {st === "filed" && <span data-testid={`${p(rt)}-arn`} className="text-xs font-mono text-emerald-300">ARN {doc.arn}</span>}
    </div>
  );
}

function Header({ title, doc, children }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <span className="font-head text-lg font-semibold">{title}</span>
          <StatusBadge status={doc?.status || "draft"} testid={`${title.toLowerCase().replace(/[^a-z0-9]/g, "")}-status`} />
        </div>
        <div className="small-label mt-1">Due {doc?.due_date || "—"}{doc?.prepared_by_name ? ` · Prepared by: ${doc.prepared_by_name}` : ""}{doc?.approved_by_name ? ` · Approved by: ${doc.approved_by_name}` : ""}</div>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Checks({ checks }) {
  if (!checks?.length) return <div data-testid="gstr9-checks-ok" className="text-xs font-mono text-emerald-400">All matching checks passed</div>;
  return (
    <div className="space-y-1" data-testid="gstr9-checks">
      {checks.map((c) => (
        <div key={c.code} className={`flex items-center gap-2 text-xs font-mono ${c.level === "error" ? "text-rose-300" : "text-amber-300"}`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> [{c.code}] {c.msg}
        </div>
      ))}
    </div>
  );
}

function Gstr9Table({ meta, doc, edits, setEdits, editable }) {
  const val = (k, f) => edits[k]?.[f] ?? doc.rows[k][f];
  const parts = [...new Set(meta.map((m) => m.part))];
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <table className="w-full text-[11px] font-mono" data-testid="gstr9-table">
        <thead className="text-muted-foreground border-b border-border">
          <tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:text-left"><th>Row</th><th>Description</th>{FIELDS.map(([, l]) => <th key={l} className="text-right">{l}</th>)}</tr>
        </thead>
        <tbody>
          {parts.map((part) => (
            <React.Fragment key={part}>
              <tr className="bg-secondary/40"><td colSpan={7} className="px-2 py-1.5 small-label">{part}</td></tr>
              {meta.filter((m) => m.part === part).map((m) => {
                const total = m.kind === "total";
                const showTaxable = part.includes("· 4") || part.includes("· 5");
                return (
                  <tr key={m.key} data-testid={`gstr9-row-${m.key}`} className={`border-b border-border/40 ${total ? "bg-indigo-500/5 font-semibold" : ""}`}>
                    <td className="px-2 py-1">{m.key}</td>
                    <td className="px-2 py-1 min-w-[260px] font-sans">{m.label}</td>
                    {FIELDS.map(([f]) => (
                      <td key={f} className="px-1 py-1 text-right">
                        {f === "taxable" && !showTaxable ? "" : editable && !total ? (
                          <input data-testid={`gstr9-input-${m.key}-${f}`} value={val(m.key, f)}
                            onChange={(e) => setEdits({ ...edits, [m.key]: { ...edits[m.key], [f]: e.target.value } })}
                            className={`w-24 text-right rounded bg-secondary/50 border px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${doc.overrides?.[m.key]?.[f] !== undefined ? "border-amber-500/50" : "border-border"}`} />
                        ) : inr(doc.rows[m.key][f])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HsnTable({ title, rows, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid={testid}>
      <div className="px-3 py-2 small-label border-b border-border">{title}</div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground"><tr className="[&>th]:px-2 [&>th]:py-1.5 [&>th]:text-left"><th>HSN</th><th>Rate</th><th>Docs</th>{FIELDS.map(([, l]) => <th key={l} className="text-right">{l}</th>)}</tr></thead>
        <tbody>
          {rows.map((h) => (
            <tr key={`${h.hsn}-${h.rate}`} className="border-t border-border/40 [&>td]:px-2 [&>td]:py-1"><td>{h.hsn}</td><td>{h.rate}%</td><td>{h.count}</td>{FIELDS.map(([f]) => <td key={f} className="text-right">{inr(h[f])}</td>)}</tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-2 py-3 text-center text-muted-foreground">No data for this year</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Gstr9View({ clientId, fy, role, meta, onChange }) {
  const [doc, setDoc] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const base = `/clients/${clientId}/annual/gstr9`;
  const load = useCallback(() => {
    api.get(`${base}?fy=${fy}`).then((r) => { setDoc(r.data); setEdits({}); }).catch((e) => toast.error(apiError(e)));
  }, [base, fy]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); setDoc(data); setEdits({}); toast.success(msg); onChange(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const compute = () => run(() => api.post(`${base}/compute?fy=${fy}`), "GSTR-9 computed from GSTR-1 / 3B / 2B");
  const save = () => run(() => api.put(`${base}/overrides?fy=${fy}`, { overrides: edits }), "Edits saved");
  const upload = (f) => { const fd = new FormData(); fd.append("file", f); run(() => api.post(`${base}/template?fy=${fy}`, fd), "Template uploaded"); };

  if (!doc) return <div className="text-muted-foreground font-mono text-sm">Loading GSTR-9…</div>;
  const canEdit = role !== "reader" && ["draft", "prepared"].includes(doc.status);
  const editable = canEdit && doc.status === "prepared";
  return (
    <div className="space-y-4">
      <Header title="GSTR-9" doc={doc}>
        {canEdit && <button data-testid="gstr9-compute-button" disabled={busy} onClick={compute} className={`${btn} bg-indigo-600 hover:bg-indigo-500 text-white`}><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Fetch &amp; Compute</button>}
        {editable && Object.keys(edits).length > 0 && <button data-testid="gstr9-save-button" disabled={busy} onClick={save} className={`${btn} bg-emerald-600 hover:bg-emerald-500 text-white`}><Save className="h-3.5 w-3.5" /> Save Edits</button>}
        <button data-testid="gstr9-download-button" onClick={() => downloadBlob(`${base}/export?fy=${fy}`, `${fy}_GSTR-9.xlsx`)} className={`${btn} border border-border hover:border-indigo-500/40`}><Download className="h-3.5 w-3.5" /> Download Excel / Template</button>
        {editable && <UploadButton testid="gstr9-upload-input" label="Upload Template" onFile={upload} disabled={busy} />}
      </Header>
      <p className="text-xs text-muted-foreground">{doc.status === "draft" ? "Preview from books — click Fetch & Compute to prepare and enable editing." : `Source: ${doc.source}. Yellow-bordered cells are your manual edits.`}</p>
      <Checks checks={doc.checks} />
      <WorkflowBar clientId={clientId} rt="GSTR9" keyStr={fy} doc={doc.status === "draft" ? null : doc} role={role} onDone={() => { load(); onChange(); }} />
      <Gstr9Table meta={meta.rows} doc={doc} edits={edits} setEdits={setEdits} editable={editable} />
      <div className="grid gap-4 lg:grid-cols-2">
        <HsnTable title="Table 17 · HSN summary of outward supplies" rows={doc.hsn_outward || []} testid="gstr9-hsn-outward" />
        <HsnTable title="Table 18 · HSN summary of inward supplies" rows={doc.hsn_inward || []} testid="gstr9-hsn-inward" />
      </div>
    </div>
  );
}

function Gstr9cView({ clientId, fy, role, meta, onChange }) {
  const [doc, setDoc] = useState(null);
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/clients/${clientId}/annual/gstr9c?fy=${fy}`).then((r) => { setDoc(r.data); setInputs(r.data.inputs || {}); }).catch((e) => toast.error(apiError(e)));
  }, [clientId, fy]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/clients/${clientId}/annual/gstr9c?fy=${fy}`, { inputs }); setDoc(data); toast.success("GSTR-9C reconciled"); onChange(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  if (!doc) return null;
  const editable = role !== "reader" && ["draft", "prepared"].includes(doc.status);
  const res = doc.result;
  return (
    <div className="space-y-4">
      <Header title="GSTR-9C" doc={doc}>
        {editable && <button data-testid="gstr9c-save-button" disabled={busy} onClick={save} className={`${btn} bg-indigo-600 hover:bg-indigo-500 text-white`}><Save className="h-3.5 w-3.5" /> Save &amp; Reconcile</button>}
      </Header>
      {!res && <p data-testid="gstr9c-needs-gstr9" className="text-xs text-amber-300 font-mono">Compute GSTR-9 first — GSTR-9C reconciles your audited books against it.</p>}
      <WorkflowBar clientId={clientId} rt="GSTR9C" keyStr={fy} doc={doc.status === "draft" ? null : doc} role={role} onDone={() => { load(); onChange(); }} />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-4 space-y-2">
          <div className="small-label">Figures as per audited books</div>
          {meta.c_inputs.map((c) => (
            <div key={c.key} className="flex items-center gap-2">
              <span className="font-mono text-[11px] w-16 text-muted-foreground">{c.key}</span>
              <span className="text-xs flex-1">{c.label}</span>
              <input data-testid={`gstr9c-input-${c.key}`} disabled={!editable} value={inputs[c.key] ?? ""} onChange={(e) => setInputs({ ...inputs, [c.key]: e.target.value })}
                className="w-32 text-right rounded bg-secondary/50 border border-border px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
            </div>
          ))}
        </div>
        {res && (
          <div className="rounded-lg border border-border bg-card p-4 space-y-4 font-mono text-xs" data-testid="gstr9c-result">
            {[["Turnover reconciliation (Table 5)", res.turnover], ["Tax paid (Table 9)", res.tax], ["ITC reconciliation (Table 12)", res.itc]].map(([t, sec]) => (
              <div key={t}>
                <div className="small-label mb-1">{t}</div>
                {Object.entries(sec).map(([k, v], i, arr) => (
                  <div key={k} className={`flex justify-between py-0.5 ${i === arr.length - 1 ? `border-t border-border pt-1 ${Math.abs(v) > 1 ? "text-rose-300" : "text-emerald-300"}` : ""}`}>
                    <span className="text-muted-foreground">{k.replace(/_/g, " ")}</span><span>{inr(v)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Itc04View({ clientId, fy, role, onChange }) {
  const [half, setHalf] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("half");
    return ["H1", "H2", "FY"].includes(q) ? q : "H1";
  });
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/clients/${clientId}/annual/itc04`;
  const key = `${fy}-${half}`;
  const load = useCallback(() => {
    api.get(`${base}?fy=${fy}&half=${half}`).then((r) => setDoc(r.data)).catch((e) => toast.error(apiError(e)));
  }, [base, fy, half]);
  useEffect(() => { load(); }, [load]);
  const upload = async (f) => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post(`${base}/upload?fy=${fy}&half=${half}`, fd); setDoc(data); toast.success(`Uploaded · ${data.summary.invalid_count} invalid row(s)`); onChange(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const clear = async () => {
    if (!window.confirm("Clear uploaded ITC-04 data for this period?")) return;
    try { await api.delete(`${base}?fy=${fy}&half=${half}`); load(); onChange(); } catch (e) { toast.error(apiError(e)); }
  };
  if (!doc) return null;
  const editable = role !== "reader" && ["draft", "prepared"].includes(doc.status);
  const s = doc.summary;
  return (
    <div className="space-y-4">
      <Header title="ITC-04" doc={doc}>
        <select data-testid="itc04-half-select" value={half} onChange={(e) => setHalf(e.target.value)} className="rounded-md bg-secondary/50 border border-border px-2 py-1.5 text-xs font-mono">
          <option value="H1">Apr–Sep (H1)</option><option value="H2">Oct–Mar (H2)</option><option value="FY">Annual (FY)</option>
        </select>
        <button data-testid="itc04-template-button" onClick={() => downloadBlob(`${base}/template`, "ITC-04_Template.xlsx")} className={`${btn} border border-border hover:border-indigo-500/40`}><FileDown className="h-3.5 w-3.5" /> Blank Template</button>
        {editable && <UploadButton testid="itc04-upload-input" label="Upload Filled Template" onFile={upload} disabled={busy} />}
        {s && <button data-testid="itc04-download-button" onClick={() => downloadBlob(`${base}/export?fy=${fy}&half=${half}`, `${key}_ITC-04.xlsx`)} className={`${btn} border border-border hover:border-indigo-500/40`}><Download className="h-3.5 w-3.5" /> Download (pre-upload check)</button>}
        {editable && s && <button data-testid="itc04-clear-button" onClick={clear} className={`${btn} border border-rose-500/40 text-rose-300`}>Clear</button>}
      </Header>
      <WorkflowBar clientId={clientId} rt="ITC04" keyStr={key} doc={doc.status === "draft" ? null : doc} role={role} onDone={() => { load(); onChange(); }} />
      {!s && <p className="text-xs text-muted-foreground">Download the blank template, fill Table 4 (goods sent to job worker) and Table 5A (received back), then upload it.</p>}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-xs" data-testid="itc04-summary">
          <div className="rounded-md bg-secondary/40 p-3"><div className="small-label">Challans sent</div><div className="text-base mt-1">{s.sent_count}</div></div>
          <div className="rounded-md bg-secondary/40 p-3"><div className="small-label">Received back</div><div className="text-base mt-1">{s.received_count}</div></div>
          <div className="rounded-md bg-secondary/40 p-3"><div className="small-label">Value sent</div><div className="text-base mt-1">{inr(s.sent_taxable)}</div></div>
          <div className="rounded-md bg-secondary/40 p-3"><div className="small-label">Invalid rows</div><div className={`text-base mt-1 ${s.invalid_count ? "text-rose-300" : "text-emerald-300"}`} data-testid="itc04-invalid-count">{s.invalid_count}</div></div>
        </div>
      )}
      {s && <Itc04Rows title="Table 4 · Goods sent to job worker" rows={doc.sent} cols={["jw_gstin", "challan_no", "challan_date", "description", "qty", "taxable_value"]} testid="itc04-sent-table" />}
      {s && <Itc04Rows title="Table 5A · Received back from job worker" rows={doc.received} cols={["jw_gstin", "orig_challan_no", "jw_challan_no", "jw_challan_date", "qty", "losses_qty"]} testid="itc04-received-table" />}
      {s?.pending_challans?.length > 0 && <Itc04Rows title="Pending with job worker" rows={s.pending_challans} cols={["job_worker", "challan_no", "sent", "received", "pending"]} testid="itc04-pending-table" noStatus />}
    </div>
  );
}

function Itc04Rows({ title, rows, cols, testid, noStatus }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid={testid}>
      <div className="px-3 py-2 small-label border-b border-border">{title}</div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground"><tr className="[&>th]:px-2 [&>th]:py-1.5 [&>th]:text-left">{cols.map((c) => <th key={c}>{c.replace(/_/g, " ")}</th>)}{!noStatus && <th>Status</th>}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border/40 [&>td]:px-2 [&>td]:py-1">
              {cols.map((c) => <td key={c}>{typeof r[c] === "number" && c.includes("value") ? inr(r[c]) : String(r[c] ?? "")}</td>)}
              {!noStatus && <td className={r.valid ? "text-emerald-400" : "text-rose-300"}>{r.valid ? "valid" : (r.errors || []).join("; ")}</td>}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="px-2 py-3 text-center text-muted-foreground">No rows</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function AnnualReturnsPanel({ clientId, role }) {
  const [fy, setFy] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("fy");
    return FY_OPTIONS.includes(q) ? q : FY_OPTIONS[1];
  });
  const [view, setView] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("view");
    return ["gstr9", "gstr9c", "itc04"].includes(q) ? q : "gstr9";
  });
  const [meta, setMeta] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const loadStatus = useCallback(() => {
    api.get(`/clients/${clientId}/annual/status?fy=${fy}`).then((r) => setStatuses(r.data)).catch(() => {});
  }, [clientId, fy]);
  useEffect(() => { api.get(`/clients/${clientId}/annual/meta`).then((r) => setMeta(r.data)).catch((e) => toast.error(apiError(e))); }, [clientId]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  if (!meta) return <div className="text-muted-foreground font-mono text-sm">Loading…</div>;
  const st = (rt) => statuses.filter((s) => s.return_type === rt && (rt !== "ITC04" || s.key === `${fy}-H1`)).map((s) => s.status)[0];
  const props = { clientId, fy, role, meta, onChange: loadStatus };
  return (
    <div className="space-y-5" data-testid="annual-returns-panel">
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="annual-fy-select" value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-md bg-secondary/50 border border-border px-3 py-1.5 text-sm font-mono">
          {FY_OPTIONS.map((f) => <option key={f} value={f}>FY {f}</option>)}
        </select>
        {[["gstr9", "GSTR-9", "GSTR9"], ["gstr9c", "GSTR-9C", "GSTR9C"], ["itc04", "ITC-04", "ITC04"]].map(([k, label, rt]) => (
          <button key={k} data-testid={`annual-view-${k}`} onClick={() => setView(k)}
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-mono border ${view === k ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" : "border-border text-muted-foreground"}`}>
            {label}{st(rt) && <StatusBadge status={st(rt)} className="text-[9px] px-1" />}
          </button>
        ))}
      </div>
      {view === "gstr9" && <Gstr9View key={`9-${fy}`} {...props} />}
      {view === "gstr9c" && <Gstr9cView key={`9c-${fy}`} {...props} />}
      {view === "itc04" && <Itc04View key={`04-${fy}`} {...props} />}
    </div>
  );
}
